import { Box, Typography, Popover } from "@mui/material";
import { format, startOfMonth, parseISO, endOfMonth } from "date-fns";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import { FC, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { IconComponent, LoadingButton } from "src/shared/components";
import { advertisingServiceLogos } from "src/shared/constants/company.constants";
import { Can } from "src/shared/lib/rback/Can";
import { EIconNames } from "src/shared/types/iconNames";
import {
  useLazyGetErpPartnerClientsShowBonusesQuery,
  useLazyExportErpPartnerClientsAccumulationQuery,
} from "../../api/api";
import { IServiceData } from "../../model/types/types";

interface ITableHeaderProps {
  serviceName: string;
  serviceData: IServiceData;
  index?: number;
}
interface IBonusItem {
  spends_from?: number;
  spends_percentage?: number;
}

export const TableHeader: FC<ITableHeaderProps> = ({
  serviceName,
  serviceData,
  index,
}) => {
  const [bonusesGrid, setBonusesGrid] = useState<IBonusItem[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { partner_id } = useParams();
  const [searchParams] = useSearchParams();
  const [exportErpPartnerClientsAccumulation, { isFetching }] =
    useLazyExportErpPartnerClientsAccumulationQuery();
  const [getErpPartnerClientsShowBonuses, { isFetching: isBonusesFetching }] =
    useLazyGetErpPartnerClientsShowBonusesQuery();
  const handleClosePopover = () => {
    setAnchorEl(null);
  };
  const isPopoverOpen = Boolean(anchorEl);
  const popoverId = isPopoverOpen
    ? `bonuses-popover-${serviceData.virtual_contract_id}`
    : undefined;
  const handleErpPartnerClientsShowBonuses = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    setAnchorEl(event.currentTarget);

    try {
      const queryParams = {
        partner_id,
        virtual_contract_id: serviceData.virtual_contract_id,
      };

      const result =
        await getErpPartnerClientsShowBonuses(queryParams).unwrap();
      const bonuses = result.data?.partner_bonuses_percents_grid?.length
        ? result.data.partner_bonuses_percents_grid
        : result.data?.default_bonuses_percents_grid ?? [];

      if (bonuses) {
        setBonusesGrid(bonuses);
      }
    } catch (err) {
      enqueueSnackbar("Произошла ошибка", {
        variant: "error",
        handleClose: closeSnackbar,
      });
    }
  };

  const YMD = "yyyy-MM-dd";

  const toStartDate = (ym: string) =>
    format(startOfMonth(parseISO(`${ym}-01`)), YMD);

  const toEndDate = (ym: string) =>
    format(endOfMonth(parseISO(`${ym}-01`)), YMD);

  const handleErpPartnerClientsAccumulationExport = async () => {
    const start_date = toStartDate(searchParams.get("month_from")!);
    const end_date = toEndDate(searchParams.get("month_to")!);
    const FILE_NAME = "Отчет_по_клиентам_партнера_";

    if (!start_date || !end_date) {
      enqueueSnackbar("Выберите месяц/месяцы для экспорта", {
        variant: "warning",
        handleClose: closeSnackbar,
      });

      return;
    }

    try {
      const queryParams = {
        partner_id,
        start_date,
        end_date,
      };
      const result = await exportErpPartnerClientsAccumulation(queryParams);

      if (result.data) {
        const url = window.URL.createObjectURL(new Blob([result.data]));
        const link = document.createElement("a");
        const resultFileName = `${FILE_NAME}${start_date}-${end_date}`;
        link.href = url;
        link.setAttribute("download", `${resultFileName}.csv`); // Имя файла
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      enqueueSnackbar("Произошла ошибка", {
        variant: "error",
        handleClose: closeSnackbar,
      });
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        mb: 2,
      }}
    >
      <Box sx={{ display: "flex", gap: 3, alignItems: "center" }}>
        <Typography variant="h6">
          <IconComponent
            sx={{ minWidth: 16, width: 16, height: 16, margin: 2 }}
            iconname={
              advertisingServiceLogos[
                serviceName as keyof typeof advertisingServiceLogos
              ] || EIconNames.WEBCOM_LOGO_SHORT
            }
          />
          {serviceData.virtual_contract_co_name}
        </Typography>
        <Typography variant="h6">
          Номер договора: {serviceData.virtual_contract_number}
        </Typography>
        <Can perform={["crm.partners.partner-clients"]}>
          <LoadingButton
            sx={{ ml: 7 }}
            variant="outlined"
            aria-describedby={popoverId}
            onClick={handleErpPartnerClientsShowBonuses}
            isLoading={isBonusesFetching}
            disabled={isBonusesFetching}
          >
            Показать бонусы
          </LoadingButton>
        </Can>
        <Popover
          id={popoverId}
          open={isPopoverOpen}
          anchorEl={anchorEl}
          onClose={handleClosePopover}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          <Box sx={{ p: 3 }}>
            {isBonusesFetching ? (
              <Typography>Загрузка...</Typography>
            ) : (
              bonusesGrid.map((item) => (
                <Typography
                  key={`${item.spends_from}-${item.spends_percentage}`}
                  sx={{ m: 2 }}
                >
                  Сумма от {item.spends_from} BYN – Бонус{" "}
                  {item.spends_percentage}%
                </Typography>
              ))
            )}
          </Box>
        </Popover>
      </Box>
      {index === 0 && (
        <LoadingButton
          variant="contained"
          onClick={handleErpPartnerClientsAccumulationExport}
          isLoading={isFetching}
          disabled={isFetching}
        >
          Экспорт данных
        </LoadingButton>
      )}
    </Box>
  );
};
